import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listDockActivity, upsertDockActivity, setDockStatus, deleteDockActivity,
} from "@/lib/dock.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import {
  ArrowDown, ArrowUp, Plus, Truck, Clock, CheckCircle2, LogIn, LogOut, Trash2, Loader2, Container,
} from "lucide-react";
import { toast } from "sonner";
import { DockYardTabs } from "@/components/dock-yard-tabs";

export const Route = createFileRoute("/_authenticated/app/dock")({ component: DockSchedulePage });

type Row = {
  id: string;
  facility_id: string;
  dock_door: string | null;
  kind: "inbound" | "outbound";
  carrier: string | null;
  trailer_ref: string | null;
  scheduled_at: string | null;
  arrived_at: string | null;
  departed_at: string | null;
  status: "scheduled" | "arrived" | "working" | "departed" | "cancelled";
  notes: string | null;
  shipment_id: string | null;
  asn_id: string | null;
};

function todayIso() { return new Date().toISOString().slice(0, 10); }
function tomorrowIso() { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); }
function fmtTime(s: string | null) { return s ? new Date(s).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"; }

function DockSchedulePage() {
  const { tenantId, facilityId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listDockActivity);
  const saveFn = useServerFn(upsertDockActivity);
  const statusFn = useServerFn(setDockStatus);
  const delFn = useServerFn(deleteDockActivity);

  const [date, setDate] = useState<string>(todayIso());
  const [kindFilter, setKindFilter] = useState<"all" | "inbound" | "outbound">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "scheduled" | "arrived" | "working" | "departed" | "cancelled">("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);

  const enabled = !!tenantId;
  const queryKey = ["dock-activity", tenantId, facilityId, date, kindFilter, statusFilter];
  const { data, isLoading } = useQuery({
    queryKey,
    enabled,
    queryFn: () => listFn({ data: {
      tenantId: tenantId!,
      facilityId: facilityId ?? undefined,
      fromDate: `${date}T00:00:00Z`,
      toDate: `${date}T23:59:59Z`,
      kind: kindFilter === "all" ? undefined : kindFilter,
      status: statusFilter === "all" ? undefined : statusFilter,
    } }),
  });

  const rows: Row[] = (data?.rows ?? []) as Row[];

  const byDoor = useMemo(() => {
    const m = new Map<string, Row[]>();
    for (const r of rows) {
      const key = r.dock_door?.trim() || "Unassigned";
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(r);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
  }, [rows]);

  const totals = useMemo(() => ({
    total: rows.length,
    inbound: rows.filter((r) => r.kind === "inbound").length,
    outbound: rows.filter((r) => r.kind === "outbound").length,
    onsite: rows.filter((r) => r.status === "arrived" || r.status === "working").length,
  }), [rows]);

  const advance = async (id: string, status: Row["status"]) => {
    try { await statusFn({ data: { id, status } }); qc.invalidateQueries({ queryKey: ["dock-activity"] }); }
    catch (e) { toast.error((e as Error).message); }
  };
  const remove = async (id: string) => {
    if (!confirm("Delete this dock appointment?")) return;
    await delFn({ data: { id } });
    qc.invalidateQueries({ queryKey: ["dock-activity"] });
  };
  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (r: Row) => { setEditing(r); setDialogOpen(true); };

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Dock Schedule"
        description="Inbound and outbound appointments by door"
        actions={<Button onClick={openNew}><Plus className="h-4 w-4 mr-2" />New appointment</Button>}
      />
      <DockYardTabs />

      <div className="px-4 sm:px-6 pt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Appointments" value={totals.total} />
        <Stat label="Inbound" value={totals.inbound} icon={<ArrowDown className="h-3.5 w-3.5" />} />
        <Stat label="Outbound" value={totals.outbound} icon={<ArrowUp className="h-3.5 w-3.5" />} />
        <Stat label="On site" value={totals.onsite} icon={<Truck className="h-3.5 w-3.5" />} />
      </div>

      <div className="px-4 sm:px-6 pt-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-44" />
        </div>
        <div>
          <Label className="text-xs">Kind</Label>
          <Select value={kindFilter} onValueChange={(v) => setKindFilter(v as any)}>
            <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="inbound">Inbound</SelectItem>
              <SelectItem value="outbound">Outbound</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
            <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="arrived">Arrived</SelectItem>
              <SelectItem value="working">Working</SelectItem>
              <SelectItem value="departed">Departed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-4">
        {isLoading ? (
          <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">Loading…</CardContent></Card>
        ) : byDoor.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">No appointments for this day.</CardContent></Card>
        ) : byDoor.map(([door, items]) => (
          <Card key={door}>
            <CardHeader className="py-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium">
                Door {door} <span className="text-muted-foreground font-normal">· {items.length} appt{items.length === 1 ? "" : "s"}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">Time</TableHead>
                    <TableHead className="w-24">Kind</TableHead>
                    <TableHead>Carrier / Trailer</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="w-28">Status</TableHead>
                    <TableHead className="w-28">Arrived</TableHead>
                    <TableHead className="w-28">Departed</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => (
                    <TableRow key={r.id} className="text-sm">
                      <TableCell className="font-mono">{fmtTime(r.scheduled_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-normal">
                          {r.kind === "inbound" ? <ArrowDown className="h-3 w-3 mr-1" /> : <ArrowUp className="h-3 w-3 mr-1" />}
                          {r.kind}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <button className="text-left hover:underline" onClick={() => openEdit(r)}>
                          <div>{r.carrier ?? "—"}</div>
                          <div className="text-xs text-muted-foreground font-mono">{r.trailer_ref ?? "—"}</div>
                        </button>
                        {r.trailer_ref && (
                          <Link
                            to="/app/yard"
                            className="text-[10px] uppercase tracking-wide text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5 mt-0.5"
                          >
                            <Container className="h-3 w-3" /> view in yard
                          </Link>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.shipment_id ? (
                          <Link to="/app/shipments/$shipmentId" params={{ shipmentId: r.shipment_id }} className="font-mono hover:underline">
                            SHP {r.shipment_id.slice(0, 8)}
                          </Link>
                        ) : r.asn_id ? (
                          <span className="font-mono">ASN {r.asn_id.slice(0, 8)}</span>
                        ) : (r.notes ? <span className="text-muted-foreground">{r.notes}</span> : <span className="text-muted-foreground">—</span>)}
                      </TableCell>
                      <TableCell><StatusPill tone={statusTone(r.status)} dot>{r.status}</StatusPill></TableCell>
                      <TableCell className="font-mono text-xs">{fmtTime(r.arrived_at)}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtTime(r.departed_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {r.status === "scheduled" && (
                            <Button size="sm" variant="outline" onClick={() => advance(r.id, "arrived")}>
                              <LogIn className="h-3.5 w-3.5 mr-1" />Check in
                            </Button>
                          )}
                          {r.status === "arrived" && (
                            <Button size="sm" variant="outline" onClick={() => advance(r.id, "working")}>
                              <Clock className="h-3.5 w-3.5 mr-1" />Working
                            </Button>
                          )}
                          {r.status === "working" && (
                            <Button size="sm" onClick={() => advance(r.id, "departed")}>
                              <LogOut className="h-3.5 w-3.5 mr-1" />Depart
                            </Button>
                          )}
                          {r.status === "departed" && (
                            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                          )}
                          <Button size="sm" variant="ghost" onClick={() => remove(r.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))}
      </div>

      <DockEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        defaultDate={date}
        tenantId={tenantId}
        facilityId={facilityId}
        onSave={async (payload) => {
          await saveFn({ data: payload });
          qc.invalidateQueries({ queryKey: ["dock-activity"] });
        }}
      />
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: number; icon?: React.ReactNode }) {
  return (
    <div className="rounded-md border bg-card px-3 py-2">
      <div className="text-xs text-muted-foreground flex items-center gap-1">{icon}{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function DockEditDialog({
  open, onOpenChange, editing, defaultDate, tenantId, facilityId, onSave,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  editing: Row | null;
  defaultDate: string;
  tenantId: string | null;
  facilityId: string | null;
  onSave: (p: any) => Promise<void>;
}) {
  const isEdit = !!editing;
  const [kind, setKind] = useState<"inbound" | "outbound">(editing?.kind ?? "outbound");
  const [door, setDoor] = useState(editing?.dock_door ?? "");
  const [carrier, setCarrier] = useState(editing?.carrier ?? "");
  const [trailer, setTrailer] = useState(editing?.trailer_ref ?? "");
  const [when, setWhen] = useState(editing?.scheduled_at?.slice(0, 16) ?? `${defaultDate}T09:00`);
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [busy, setBusy] = useState(false);

  // Reset on open
  useMemo(() => {
    if (!open) return;
    setKind(editing?.kind ?? "outbound");
    setDoor(editing?.dock_door ?? "");
    setCarrier(editing?.carrier ?? "");
    setTrailer(editing?.trailer_ref ?? "");
    setWhen(editing?.scheduled_at?.slice(0, 16) ?? `${defaultDate}T09:00`);
    setNotes(editing?.notes ?? "");
  }, [open, editing, defaultDate]);

  const submit = async () => {
    if (!tenantId || !facilityId) { toast.error("Select a tenant & facility first"); return; }
    setBusy(true);
    try {
      await onSave({
        id: editing?.id,
        tenantId, facilityId,
        kind,
        dockDoor: door || null,
        carrier: carrier || null,
        trailerRef: trailer || null,
        scheduledAt: when ? new Date(when).toISOString() : null,
        notes: notes || null,
      });
      toast.success(isEdit ? "Updated" : "Scheduled");
      onOpenChange(false);
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{isEdit ? "Edit appointment" : "New appointment"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Kind</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="outbound">Outbound</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Door</Label>
            <Input value={door} onChange={(e) => setDoor(e.target.value)} placeholder="D-12" />
          </div>
          <div>
            <Label className="text-xs">Scheduled</Label>
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Carrier</Label>
            <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="UPS, ODFL, …" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Trailer / reference</Label>
            <Input value={trailer} onChange={(e) => setTrailer(e.target.value)} placeholder="T-44892 · seal 11023" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Live-load, lift-gate, hazmat..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {isEdit ? "Save" : "Schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Silence unused import (kept to satisfy linter when referenced via Dialog API)
void DialogTrigger;
void tomorrowIso;